import type { Notification } from '../../models/general';
import type { PendingActionStyle } from '../../api/pendingActions';

/**
 * A notification the server tagged as actionable.
 *
 * `src/models/general.ts` is generated, so this is the one place the two
 * quick-action fields are described for the notification surfaces. The generated
 * `Notification` already carries them as optional, so this alias is a naming and
 * documentation device rather than a widening.
 *
 * Contract (see NotificationService.Create):
 *  - `actionSource`   — a PendingActionSources constant, never a free string.
 *  - `actionEntityId` — byte-identical to the id the PendingActions aggregator
 *                       emits for the same entity, so `${source}:${id}` matches.
 * Both are null on every pre-existing row and on every informational type.
 */
export type ActionableNotification = Notification & {
  actionSource?: string | null;
  actionEntityId?: string | null;
};

/** The hydration key for a notification, or null when it carries no action. */
export function notificationActionKey(n: ActionableNotification): string | null {
  if (!n.actionSource || !n.actionEntityId) return null;
  return `${n.actionSource}:${n.actionEntityId}`;
}

/**
 * The outcome shown in place of the buttons once a row has been decided here.
 * The action style is kept so the past-tense wording matches the verb the user
 * pressed ("Approved" after Approve, "Accepted" after Accept).
 */
export interface ResolvedOutcome {
  positive: boolean;
  actionStyle: PendingActionStyle;
}

/** The past-tense i18n key for a decision the user just took. */
export function resolvedOutcomeLabel(outcome: ResolvedOutcome): string {
  if (outcome.actionStyle === 'Complete1') return 'Completed';
  if (outcome.actionStyle === 'Accept2') return outcome.positive ? 'Accepted' : 'Declined';
  return outcome.positive ? 'Approved' : 'Rejected';
}
